/**
 * The price bound a market order is willing to accept.
 *
 * Traders think in percent ("1% max"); the order path speaks basis points, an
 * integer, because a bound is multiplied into a price on-chain and a float
 * percentage does not survive that trip (1.1% as a double is 0.011000000000000001).
 * So: percent in, bps out, converted through `dec` and never through `Number`
 * arithmetic — 0.5% is exactly 50 bps, 0.01% exactly 1.
 *
 * Both tails are called out, because both lose money in different ways: a wide
 * bound is what gets filled at a price nobody wanted, and a bound tighter than
 * the spread is an order that simply never fills.
 *
 * It also states the CONSEQUENCE when the caller can supply one. A percentage
 * on its own is not information a trader can act on; "fills no worse than
 * 60,600" is. The caller does that maths (`worstFillPrice`) because only the
 * caller knows the side and which side of the book to read — this control
 * stays presentational. It reports a number; what the order path does with it
 * is not its business — today some chains cannot bound a market buy at all,
 * and those pass `unsupportedNote` instead of pretending otherwise.
 *
 * Collapsed by default where it sits in a dense form, because most traders set
 * this once. Settings passes `collapsible={false}`: there it is the subject of
 * the screen, not a detail of it.
 */
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { COLORS } from "../../../constants/design/colors";
import { BORDER_RADIUS, SPACING } from "../../../constants/design/spacing";
import { Chevron } from "../Chevron/style";
import { dec } from "../../../utils/amount/decimal";
import {
  HIGH_SLIPPAGE_WARN_BPS,
  LOW_SLIPPAGE_WARN_BPS,
  SLIPPAGE_PRESETS_BPS,
} from "../../../utils/amount/slippage";
import { slippageSpec } from "../../../utils/amount/spec";
import { InputMeta } from "./InputMeta";
import { NumericInput } from "./NumericInput";
import { useRovingRadioGroup } from "../../../keyboard/useRovingRadioGroup";
import {
  ActionRow,
  Chip,
  FieldWrapper,
  HintText,
  MetaLabel,
  MetaRow,
  MetaValue,
  SlotLabel,
} from "./style";

export interface SlippageConsequence {
  /** "Fills no worse than" / "Receive at least". */
  label: string;
  /** Already formatted. Absent renders as unknown, never as a zero. */
  valueText?: string;
  unit?: string;
}

export interface SlippageControlProps {
  /** The bound in basis points. 50 = 0.5%. */
  bps: number;
  onChange: (bps: number) => void;
  presets?: number[];
  disabled?: boolean;
  /** Replaces the control: this market cannot carry a price bound at all. */
  unsupportedNote?: string;
  /**
   * Collapse to a single summary row until the trader opens it. On in dense
   * surfaces (trade form, close modal), off in settings.
   */
  collapsible?: boolean;
  /** What this bound means in money, if the caller can work it out. */
  consequence?: SlippageConsequence;
}

/** Above this the trader is agreeing to a fill they would not have chosen. */
const HIGH_BPS = HIGH_SLIPPAGE_WARN_BPS;
/** Below this the bound is tighter than a typical spread; the order sits. */
const LOW_BPS = LOW_SLIPPAGE_WARN_BPS;

const DEFAULT_PRESETS: number[] = [...SLIPPAGE_PRESETS_BPS];

/**
 * A warning worth reading. This used to be grey body text appended under the
 * input, indistinguishable from a hint; a bound wide enough to lose real money
 * deserves the warning colour DESIGN.md reserves for it.
 */
const WarnRow = styled.div`
  display: flex;
  gap: ${SPACING.one}px;
  padding: ${SPACING.one}px ${SPACING.two}px;
  border-radius: ${BORDER_RADIUS.sm}px;
  background: ${COLORS.system.five};
  border-left: 2px solid ${COLORS.system.one};
  color: ${COLORS.system.one};
  font-size: 12px;
`;

/** The collapsed row: the whole thing is the disclosure. */
const SummaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.two}px;
  width: 100%;
  min-height: 32px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  &:hover span {
    color: ${COLORS.white.one};
  }
`;

const Disclosure = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.one}px;
`;

/** 50 -> "0.5". Exact: bps are hundredths of a percent, nothing to round away. */
export const pctFromBps = (bps: number): string =>
  dec.div(dec.parse(bps) ?? "0", "100", 2, "floor") ?? "0";

/** "0.5" -> 50. `null` when the text is not a bound anyone could mean. */
export const bpsFromPct = (percent: string): number | null => {
  const parsed = dec.parse(percent);
  if (parsed === null) return null;
  const bps = Number(dec.round(dec.mul(parsed, "100"), 0, "floor"));
  return Number.isFinite(bps) && bps > 0 ? bps : null;
};


export function SlippageControl({
  bps,
  onChange,
  presets = DEFAULT_PRESETS,
  disabled,
  unsupportedNote,
  collapsible = false,
  consequence,
}: SlippageControlProps) {
  const { t } = useTranslation("app", { keyPrefix: "common.inputs.slippage" });

  // What the trader is typing, if anything. While it is null the field simply
  // shows the bound in force, so a preset click is reflected immediately.
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const bodyId = `${generatedId}-slippage-body`;
  const presetRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const pick = (index: number) => {
    if (disabled) return;
    const preset = presets[index];
    if (preset === undefined) return;
    setDraft(null);
    onChange(preset);
  };
  // Shared with UnitToggle and SegmentedControl. `disabled` is a whole-control
  // flag here, so reporting every preset disabled reproduces the hand-rolled
  // version's `if (disabled) return` on every key.
  //
  // ABOVE the unsupportedNote early return, and it must stay there: a hook after
  // a conditional return is called on some renders and not others, which is the
  // hook-order bug react-hooks/rules-of-hooks exists to catch. Jest and tsc both
  // passed with it below; only the production build's ESLint caught it.
  const { itemProps: presetA11y } = useRovingRadioGroup<number>({
    values: presets,
    value: bps,
    onSelect: (preset) => pick(presets.indexOf(preset)),
    focus: (_preset, index) => presetRefs.current[index]?.focus(),
    isDisabled: () => !!disabled,
  });

  if (unsupportedNote) {
    return (
      <FieldWrapper>
        <HintText>{unsupportedNote}</HintText>
      </FieldWrapper>
    );
  }

  const commit = (text: string) => {
    setDraft(null);
    const next = bpsFromPct(text);
    // An empty or zero field is not a bound: leave the one in force alone.
    if (next !== null && next !== bps) onChange(next);
  };

  const expanded = !collapsible || open;
  const valueText = `${pctFromBps(bps)}%`;


  return (
    <FieldWrapper>
      {collapsible ? (
        <SummaryButton
          type="button"
          aria-expanded={open}
          // Only while the body exists. The panel is not rendered when
          // collapsed -- the default on both the trade form and the close
          // modal -- and an aria-controls pointing at an absent id is invalid
          // ARIA in exactly the state the attribute is there for.
          aria-controls={open ? bodyId : undefined}
          onClick={() => setOpen((prev) => !prev)}
        >
          <MetaLabel as="span">{t("label")}</MetaLabel>
          <Disclosure>
            <MetaValue as="span" $interactive={false} data-testid="slippage-value">
              {valueText}
            </MetaValue>
            <Chevron direction={open ? "up" : "down"} size="small" aria-hidden="true" />
          </Disclosure>
        </SummaryButton>
      ) : (
        <MetaRow>
          <MetaLabel>{t("label")}</MetaLabel>
          <MetaValue as="span" $interactive={false} data-testid="slippage-value">
            {valueText}
          </MetaValue>
        </MetaRow>
      )}

      {expanded ? (
        <FieldWrapper id={bodyId}>
          {/* A radiogroup, not a row of unrelated buttons: exactly one bound is
              in force, and arrow keys move between them. */}
          <ActionRow role="radiogroup" aria-label={t("label")}>
            {presets.map((preset, index) => {
              const checked = preset === bps;
              return (
                <Chip
                  key={preset}
                  ref={(node: HTMLButtonElement | null) => {
                    presetRefs.current[index] = node;
                  }}
                  type="button"
                  {...presetA11y(preset, index)}
                  $active={checked}
                  onClick={() => pick(index)}
                >
                  {pctFromBps(preset)}%
                </Chip>
              );
            })}
          </ActionRow>

          <NumericInput
            value={draft ?? pctFromBps(bps)}
            spec={slippageSpec()}
            disabled={disabled}
            ariaLabel={t("custom")}
            unitSlot={<SlotLabel>%</SlotLabel>}
            onValueChange={setDraft}
            onFinalize={commit}
          />

          {/* The point of the control: what the percentage means in money.
              An absent figure renders as an em-dash, never as a zero. */}
          {consequence ? (
            <InputMeta
              label={consequence.label}
              valueText={consequence.valueText}
              unit={consequence.unit}
            />
          ) : null}

          {bps > HIGH_BPS ? <WarnRow role="status">{t("high")}</WarnRow> : null}
          {bps > 0 && bps < LOW_BPS ? <HintText role="status">{t("low")}</HintText> : null}
        </FieldWrapper>
      ) : null}
    </FieldWrapper>
  );
}
